import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { systemRolesRoute, systemUsersRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { LoadPageSuccessProps } from 'src/app/common-components/register/models/register-actions';
import { AddUserComponent } from '../../add-user/containers/add-user.component';
import { UserOrderingOptions, UserRegisterOptions } from '../models/user-register-options';
import { UserRegisterListItem } from '../models/user-register-results.model';
import { UsersFilterOption } from '../models/users-filter-option';
import { GetUsers, GetUsers_usersConnection_items } from '../queries/generated/GetUsers';
import { UserRegisterApiService } from '../services/user-register-api.service';
import { UserRegisterSessionStorageService } from '../services/user-register-session-storage.service';
import * as UserRegisterSelectors from '../state/user-register.selectors';
import {
   createUser,
   loadAccountUsersSuccess,
   loadSystemUsersSuccess,
   setOrderingOptionsFromSessionStorage,
   userRegisterAdapterActions,
} from './user-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './user-register.reducer';

@Injectable()
export class UserRegisterEffects {
   addUser$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createUser),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddUserComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   combinedSelectors$ = combineLatest([
      this.store.pipe(select(UserRegisterSelectors.selectFilters)),
      this.store.pipe(select(selectOrderingOptions)),
      this.store.pipe(select(selectCurrentRoute)),
      this.store.pipe(select(selectQuickFilters)),
      this.store.pipe(select(UserRegisterSelectors.selectIsOnSystemPage)),
   ]);

   loadUserList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(userRegisterAdapterActions.pageEnter),
         withLatestFrom(this.combinedSelectors$),
         switchMap(([action, [filters, orderingOptions, currentRoute, quickFilters, wasPreviouslyOnSystemPage]]) => {
            const path = currentRoute.url[0].path;
            if (path === systemUsersRoute || path === systemRolesRoute) {
               const sessionOrderingOptions = this.userRegisterSessionStorageService.getUserOrderingOptions();
               if (sessionOrderingOptions) {
                  orderingOptions = sessionOrderingOptions;
                  this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
               }
               if (!wasPreviouslyOnSystemPage) {
                  this.store.dispatch(userRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadSystemUsersSuccess());
               return this.loadNewPage(filters, quickFilters, orderingOptions);
            } else {
               if (wasPreviouslyOnSystemPage) {
                  this.store.dispatch(userRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadAccountUsersSuccess());
               return this.loadNewPage(filters, quickFilters, orderingOptions);
            }
         })
      );
   });

   loadUserPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(userRegisterAdapterActions.loadPage),
         withLatestFrom(
            combineLatest([
               this.store.pipe(select(UserRegisterSelectors.selectFilters)),
               this.store.pipe(select(selectEndCursor)),
               this.store.pipe(select(selectOrderingOptions)),
               this.store.pipe(select(selectCurrentRoute)),
               this.store.pipe(select(selectQuickFilters)),
            ])
         ),
         switchMap(([action, [filters, endCursor, orderingOptions, currentRoute, quickFilters]]) => {
            const path = currentRoute.url[0].path;
            if (path === systemUsersRoute) {
               const sessionOrderingOptions = this.userRegisterSessionStorageService.getUserOrderingOptions();
               if (sessionOrderingOptions) {
                  orderingOptions = sessionOrderingOptions;
                  this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
               }
            }
            return this.userRegisterApiService.getUsers(endCursor, filters, quickFilters, orderingOptions).pipe(
               map((result) => {
                  return userRegisterAdapterActions.loadPageSuccess(
                     this.convertGetUsersResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(userRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(userRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(UserRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewPage(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(userRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(UserRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, orderingOptions, currentRoute, quickFilters]) => {
            const path = currentRoute.url[0].path;
            if (path === systemUsersRoute) {
               this.userRegisterSessionStorageService.setUserOrderingOptions(orderingOptions);
            }
            return this.loadNewPage(filters, quickFilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private dialogService: LcsDialogService,
      private store: Store<AppState>,
      private userRegisterApiService: UserRegisterApiService,
      private userRegisterSessionStorageService: UserRegisterSessionStorageService
   ) {}

   private convertGetUsersResultToLoadSuccessProps(getUsers: GetUsers): LoadPageSuccessProps<UserRegisterListItem> {
      return {
         endCursor: getUsers.usersConnection.pageInfo.endCursor,
         hasNextPage: getUsers.usersConnection.pageInfo.hasNextPage,
         items: getUsers.usersConnection.items.map(this.convertUserRegisterListItem),
         totalCount: getUsers.usersConnection.totalCount,
      };
   }

   private convertUserRegisterListItem(getUserItem: GetUsers_usersConnection_items): UserRegisterListItem {
      return {
         emailAddress: getUserItem.emailAddress,
         isActive: getUserItem.isActive,
         name: getUserItem.name,
         userId: getUserItem.userId,
         username: getUserItem.username,
         lastLoggedIn: getUserItem.lastLoggedIn,
         isLocked: getUserItem.isLocked,
      };
   }

   private loadNewPage(
      filters: UsersFilterOption[],
      quickFilters: UserRegisterOptions | null = null,
      orderingOptions: UserOrderingOptions[] | null = null
   ) {
      return this.userRegisterApiService.getUsers(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return userRegisterAdapterActions.loadSuccess(this.convertGetUsersResultToLoadSuccessProps(result.data));
         }),
         catchError((error) => {
            return of(userRegisterAdapterActions.loadFailure(error));
         })
      );
   }
}
